import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { FolderContext } from "@common/dto/folder/context";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { useMessage } from "@common/hooks/message";
import { trDts } from "@modules/datasheets/utils/i18n";
import React from "react";
import { FieldPickerField } from "./components/fields/fields.types";
import { getUniqueFieldCombination } from "@common/utils/fields";
import { FieldType } from "@common/dto/field/type";

export type FieldPickerProps = {
  dndType: string;
  selection: {
    items: Array<FieldPickerField>;
    onChange: (selectedItems: Array<FieldPickerField>) => void;
  };
  isFieldAdded: (field: FieldPickerField) => boolean;
  className?: string;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getFields: (params: { folderId?: FolderPrimaryListDto['id']; searchQuery?: string; context: number }) => Promise<Array<FieldPrimaryListDto>>;
  disabled: boolean;
  defaultFolderContext?: FolderContext;
};

export const useFieldPickerController = (props: FieldPickerProps) => {
  const { getFolders, getFields, defaultFolderContext = FolderContext.individual } = props;
  const {
    messageProps,
    showErrorMessage,
    closeMessage
  } = useMessage();
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | null>(null);
  const [fields, setFields] = React.useState<Array<FieldPrimaryListDto>>([]);
  const [fieldsLoading, setFieldsLoading] = React.useState<boolean>(false);
  const [expanded, setExpanded] = React.useState(true);

  const toggleExpanded = React.useCallback(() => {
    setExpanded(x => !x);
  }, []);

  const resolveFolderContext = React.useCallback(
    (folder?: FolderPrimaryListDto | null) =>
      folder?.fieldcontext ?? folder?.context ?? defaultFolderContext,
    [defaultFolderContext]
  );

  const initialize = React.useCallback(async () => {
    try {
      const folders = await getFolders();
      setFolders(folders);
      
      // For table datasheets, folders will be empty - auto-load subfields
      if (folders.length === 0) {
        setFieldsLoading(true);
        try {
          const fields = await getFields({ context: defaultFolderContext });
          setFields(fields);
        } catch (err) {
          console.error(err);
          showErrorMessage(trDts('Failed to fetch fields'));
        } finally {
          setFieldsLoading(false);
        }
      }
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch folders'));
    }
  }, [getFolders, getFields, defaultFolderContext, showErrorMessage]);

  React.useEffect(() => {
    initialize();
  }, [initialize]);

  const handleFolderChange = React.useCallback(async (id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    props.selection.onChange([]);
    setSelectedFolder(folder);
    setFieldsLoading(true);
    try {
      const fields = await getFields({ folderId: folder.id, context: resolveFolderContext(folder) });
      setFields(fields);
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch fields'));
    } finally {
      setFieldsLoading(false);
    }
  }, [getFields, resolveFolderContext, showErrorMessage, props.selection.onChange]);

  const handleSearch = React.useCallback(async ({ searchKeyword }: { searchKeyword: string }) => {
    setFieldsLoading(true);
    try {
      const fields = await getFields({
        folderId: selectedFolder?.id,
        searchQuery: searchKeyword,
        context: resolveFolderContext(selectedFolder),
      });
      setFields(fields);
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch fields'));
    } finally {
      setFieldsLoading(false);
    }
  }, [getFields, resolveFolderContext, selectedFolder, showErrorMessage]);

  const fieldsProcessed = React.useMemo(() => {
    // Table datasheets (TDS) have no folder tree; the editor loads the table's subfields directly.
    // In this mode we must NOT collapse subfields up to the parent table, otherwise users would
    // only see the type=8 parent table field (spreadsheet parity expects subfields).
    const isTableDatasheetMode = folders.length === 0;
    if (isTableDatasheetMode) {
      return fields.map((field) => ({
        id: getUniqueFieldCombination({
          id: field.id,
          context: field.context,
          system: field.system,
          type: field.type,
        }),
        name: field.name,
        original: field,
      }));
    }

    const tableParentsById = new Map<number, FieldPrimaryListDto>();
    const regularFields: Array<FieldPrimaryListDto> = [];

    fields.forEach((field) => {
      const parent = field.parentField;

      if (parent?.type === FieldType.table && parent.id != null) {
        if (!tableParentsById.has(parent.id)) {
          tableParentsById.set(parent.id, {
            id: parent.id,
            name: parent.name,
            context: parent.context ?? field.context,
            type: parent.type,
            system: parent.system ?? field.system,
            imageURL: parent.imageURL ?? field.imageURL,
            options: parent.options ?? field.options,
            fullPath: parent.fullPath ?? field.fullPath,
            fieldtype: field.fieldtype,
            dropdownValues: field.dropdownValues,
            parentField: parent.parentField,
          });
        }

        return;
      }

      if (field.type === FieldType.table) {
        if (!tableParentsById.has(field.id)) {
          tableParentsById.set(field.id, field);
        }
        return;
      }

      regularFields.push(field);
    });

    const tableParentFields = Array.from(tableParentsById.values());
    const finalFields = [...regularFields, ...tableParentFields];

    return finalFields.map((field) => ({
      id: getUniqueFieldCombination({
        id: field.id,
        context: field.context,
        system: field.system,
        type: field.type,
      }),
      name: field.name,
      original: field,
    }));
  }, [fields, folders.length]);

  const handleFieldAlreadyAdded = React.useCallback(() => {
    showErrorMessage(trDts("fieldExistsInTab"));
  }, [showErrorMessage]);

  return {
    fieldsLoading,
    folders,
    selectedFolder,
    fieldsProcessed,
    handleFieldAlreadyAdded,
    handleSearch,
    handleFolderChange,
    closeMessage,
    messageProps,
    expanded,
    toggleExpanded,
  };
};